import { useState, useEffect } from "react";

import { Target, TrendingUp, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface GoalProgress {
  has_goal: boolean;
  goal_id?: string;
  goal_type?: string;
  target?: {
    sessions: number;
    messages: number;
    memories: number;
    projects: number;
    total: number;
  };
  current?: {
    sessions: number;
    messages: number;
    memories: number;
    projects: number;
    total: number;
  };
  progress_percent?: number;
  is_complete?: boolean;
  period_start?: string;
  period_end?: string;
}

export const ActivityGoalsManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dailyGoal, setDailyGoal] = useState<GoalProgress | null>(null);
  const [weeklyGoal, setWeeklyGoal] = useState<GoalProgress | null>(null);
  const [monthlyGoal, setMonthlyGoal] = useState<GoalProgress | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [targetTotal, setTargetTotal] = useState(10);

  useEffect(() => {
    fetchGoals();
  }, [user]);

  const fetchGoals = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const [dailyRes, weeklyRes, monthlyRes] = await Promise.all([
        supabase.rpc('get_goal_progress', { p_user_id: user.id, p_goal_type: 'daily' }),
        supabase.rpc('get_goal_progress', { p_user_id: user.id, p_goal_type: 'weekly' }),
        supabase.rpc('get_goal_progress', { p_user_id: user.id, p_goal_type: 'monthly' }),
      ]);

      if (dailyRes.error) throw dailyRes.error;
      if (weeklyRes.error) throw weeklyRes.error;
      if (monthlyRes.error) throw monthlyRes.error;

      setDailyGoal(dailyRes.data as unknown as GoalProgress);
      setWeeklyGoal(weeklyRes.data as unknown as GoalProgress);
      setMonthlyGoal(monthlyRes.data as unknown as GoalProgress);
    } catch (error) {
      console.error('Error fetching goals:', error);
      toast.error('Failed to load activity goals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!user) return;

    try {
      const { error } = await supabase.from('activity_goals').insert({
        user_id: user.id,
        goal_type: selectedType,
        target_total_activity: targetTotal,
      });

      if (error) throw error;

      toast.success(`${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} goal created!`);
      setDialogOpen(false);
      fetchGoals();
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    }
  };

  const renderGoalCard = (goal: GoalProgress | null, type: string) => {
    if (loading) {
      return (
        <Card className="animate-content-reveal">
          <CardHeader className="space-y-2">
            <Skeleton className="h-6 w-32" speed="fast" />
            <Skeleton className="h-4 w-48" speed="fast" delay={50} />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-20" speed="fast" delay={i * 60} />
                    <Skeleton className="h-4 w-16" speed="fast" delay={i * 60 + 30} />
                  </div>
                  <Skeleton className="h-2 w-full" speed="fast" delay={i * 60 + 45} />
                </div>
              ))}
            </div>
            <Skeleton className="h-9 w-full" speed="fast" delay={250} />
          </CardContent>
        </Card>
      );
    }

    if (!goal?.has_goal) {
      return (
        <Card>
          <CardContent className="py-8 text-center">
            <Target className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-4">No {type} goal set</p>
            <Button
              onClick={() => {
                setSelectedType(type as any);
                setDialogOpen(true);
              }}
              size="sm"
            >
              Set {type} goal
            </Button>
          </CardContent>
        </Card>
      );
    }

    const progressPercent = goal.progress_percent || 0;
    const isComplete = goal.is_complete || false;

    return (
      <Card className={isComplete ? 'border-primary' : ''}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {isComplete && <CheckCircle2 className="h-5 w-5 text-primary" />}
                {type.charAt(0).toUpperCase() + type.slice(1)} Goal
              </CardTitle>
              <CardDescription>
                Target: {goal.target?.total} activities
              </CardDescription>
            </div>
            <TrendingUp className={`h-5 w-5 ${isComplete ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">
                {goal.current?.total || 0} / {goal.target?.total || 0}
              </span>
            </div>
            <Progress value={Math.min(progressPercent, 100)} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {progressPercent.toFixed(1)}% complete
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2 border-t">
            <div>
              <p className="text-xs text-muted-foreground">Sessions</p>
              <p className="text-sm font-medium">
                {goal.current?.sessions || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Messages</p>
              <p className="text-sm font-medium">
                {goal.current?.messages || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Memories</p>
              <p className="text-sm font-medium">
                {goal.current?.memories || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Projects</p>
              <p className="text-sm font-medium">
                {goal.current?.projects || 0}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Activity Goals</CardTitle>
              <CardDescription>Track your daily, weekly, and monthly targets</CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Target className="h-4 w-4 mr-2" />
                  New Goal
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Activity Goal</DialogTitle>
                  <DialogDescription>
                    Set a target for your activity and track your progress
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Goal Type</Label>
                    <Tabs value={selectedType} onValueChange={(v) => setSelectedType(v as any)}>
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="daily">Daily</TabsTrigger>
                        <TabsTrigger value="weekly">Weekly</TabsTrigger>
                        <TabsTrigger value="monthly">Monthly</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="target">Total Activity Target</Label>
                    <Input
                      id="target"
                      type="number"
                      min="1"
                      value={targetTotal}
                      onChange={(e) => setTargetTotal(Number(e.target.value))}
                      placeholder="e.g., 10"
                    />
                    <p className="text-xs text-muted-foreground">
                      Combined total of sessions, messages, memories, and projects
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleCreateGoal}>Create Goal</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="daily" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>
            <TabsContent value="daily" className="mt-4">
              {renderGoalCard(dailyGoal, 'daily')}
            </TabsContent>
            <TabsContent value="weekly" className="mt-4">
              {renderGoalCard(weeklyGoal, 'weekly')}
            </TabsContent>
            <TabsContent value="monthly" className="mt-4">
              {renderGoalCard(monthlyGoal, 'monthly')}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  );
};
